import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const login = async (email, password) => {
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      // FASE 7.4 STEP F.1 — /auth/login returns user w/o permissions[].
      // Re-hydrate via /auth/me so `can()` sees the full permission set.
      await check();
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  // FASE 7.4 STEP E — permission helpers backed by /auth/me `permissions` array.
  // The backend registry (services/permissions.py) is the source of truth. The
  // frontend NEVER rebuilds permissions from role — it only reads them.
  const permissions = useMemo(
    () => (user && user.permissions ? new Set(user.permissions) : new Set()),
    [user],
  );

  const can = useCallback(
    (permission) => (permission ? permissions.has(permission) : true),
    [permissions],
  );

  const canAny = useCallback(
    (perms) => {
      if (!perms || perms.length === 0) return true;
      for (const p of perms) if (permissions.has(p)) return true;
      return false;
    },
    [permissions],
  );

  return (
    <AuthContext.Provider value={{ user, login, logout, error, setError, check, can, canAny, permissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
